import create from "zustand";
import { COLORS } from "../components/FiltroAcompanhamentoOperacional/utils";

type ViewMode = "month" | "week" | "talhao" | "janela-execucao";

export type AgendaWindowItem = {
  id: string;
  periodoDataInicio: string;
  periodoDataFim: string;
  periodoDataInicioRaw: string;
  periodoDataFimRaw: string;
  color: string;
  operacaoClasse: string;
};

interface AgendaCampoState {
  viewMode: ViewMode;
  listWindowExecution: AgendaWindowItem[];
  selectedWindowIds: string[];
  classColorMap: Record<string, string>;

  setViewMode: (args: ViewMode) => void;
  setListWindowExecution: (args: any[]) => void;
  setSelectedWindowIds: (ids: string[]) => void;
}

function buildAgendaWindowList(
  raw: any[],
  classColorMap: Record<string, string>
): { list: AgendaWindowItem[]; classColorMap: Record<string, string> } {
  const periodMap = new Map<string, Omit<AgendaWindowItem, "color">>();

  (raw || []).forEach((item) => {
    const inicio = String(item?.periodoDataInicio || "").slice(0, 10);
    const fim = String(item?.periodoDataFim || "").slice(0, 10);
    if (!inicio || !fim) return;

    const id = `${inicio}_${fim}`;
    if (periodMap.has(id)) return;

    const [yi, mi, di] = inicio.split("-");
    const [, mf, df] = fim.split("-");

    periodMap.set(id, {
      id,
      periodoDataInicioRaw: inicio,
      periodoDataFimRaw: fim,
      periodoDataInicio: `${di}/${mi}/${yi}`,
      periodoDataFim: `${df}/${mf}/${yi}`,
      operacaoClasse: item?.operacaoClasse || "",
    });
  });

  const sortedWindows = Array.from(periodMap.values()).sort(
    (a, b) =>
      a.periodoDataInicioRaw.localeCompare(b.periodoDataInicioRaw) ||
      a.periodoDataFimRaw.localeCompare(b.periodoDataFimRaw)
  );

  // Mantém a mesma cor para a mesma classe de operação entre trocas de mês,
  // ao invés de derivar a cor da posição do item na lista do período atual.
  const nextClassColorMap = { ...classColorMap };
  const usedColors = new Set(Object.values(nextClassColorMap));
  let cursor = 0;

  const colorFor = (classe: string) => {
    if (nextClassColorMap[classe]) return nextClassColorMap[classe];

    while (
      usedColors.has(COLORS[cursor % COLORS.length]) &&
      cursor < COLORS.length * 2
    ) {
      cursor += 1;
    }

    const color = COLORS[cursor % COLORS.length] || "#20a45a";
    cursor += 1;
    usedColors.add(color);
    nextClassColorMap[classe] = color;
    return color;
  };

  const list = sortedWindows.map((window) => ({
    ...window,
    color: colorFor(window.operacaoClasse),
  }));

  return { list, classColorMap: nextClassColorMap };
}

const useAgendaCampo = create<AgendaCampoState>((set) => ({
  viewMode: "month",
  listWindowExecution: [],
  selectedWindowIds: [],
  classColorMap: {},

  setListWindowExecution: (raw) => {
    set((state) => {
      const { list: listWindowExecution, classColorMap } = buildAgendaWindowList(
        raw,
        state.classColorMap
      );
      const nextIds = listWindowExecution.map((item) => item.id);
      const nextIdsKey = nextIds.join(",");
      const prevIdsKey = state.listWindowExecution.map((item) => item.id).join(",");
      const selectedWindowIds =
        prevIdsKey === nextIdsKey ? state.selectedWindowIds : nextIds;

      return {
        listWindowExecution,
        selectedWindowIds,
        classColorMap,
      };
    });
  },

  setSelectedWindowIds: (selectedWindowIds) => {
    set({ selectedWindowIds });
  },

  setViewMode: (viewMode) => {
    set({ viewMode });
  },
}));

export default useAgendaCampo;
